import type { CadDocument } from '../model/document';
import type { BlockDef, CadEntity } from '../model/entity';
import { entityBox } from '../model/entity';
import type { Box } from '../model/geom';
import { toMm } from '../model/units';

/**
 * 룰엔진 (작업지시서 P5-01)
 *
 * ── 왜 규칙을 코드가 아니라 데이터로 두는가 ─────────────────────────────
 *
 *   통로 폭·이격 기준은 **법규와 사내 표준이 바뀌면 같이 바뀐다.**
 *   그때마다 배포해야 한다면 현장은 낡은 기준으로 검토하게 된다.
 *   → 규칙은 값(RuleSet)이고, 엔진은 값을 해석만 한다.
 *     DB(`cad_rule_set`)나 프로젝트 설정에서 갈아끼울 수 있어야 한다.
 *
 * ── 이 엔진이 하지 않는 것 ──────────────────────────────────────────────
 *
 *   **법적 적합성을 보증하지 않는다.** 도면상 기하 조건만 본다.
 *   실제 인허가는 소방·건축 검토를 따로 받아야 한다. 화면도 그렇게 말한다.
 *   여기서 "통과" 가 떴다고 인허가가 통과한다는 뜻이 아니다.
 */

export type Severity = 'error' | 'warning' | 'info';

export interface Violation {
  ruleId: string;
  severity: Severity;
  /** 사람이 읽는 문장. "무엇이 · 얼마나 · 왜" 가 들어가야 한다. */
  message: string;
  /** 관련 객체 — 클릭하면 도면에서 선택된다 */
  entityIds: string[];
  /** 화면에서 강조할 영역 (µm) */
  box: Box | null;
  /** 기준값과 실제값 (mm) — 얼마나 모자란지 보여준다 */
  requiredMm?: number;
  actualMm?: number;
}

export interface RuleContext {
  doc: CadDocument;
  blocks: Map<string, BlockDef>;
  /** 블록 코드 → 이격 요구 (mm) */
  clearanceOf: (blockCode: string) => { frontMm?: number; sideMm?: number; rearMm?: number } | undefined;
}

export interface Rule {
  id: string;
  name: string;
  /** 왜 이 규칙이 있는지. 위반 목록에서 사용자가 펼쳐 볼 수 있어야 한다. */
  rationale: string;
  severity: Severity;
  enabled: boolean;
  check: (ctx: RuleContext, params: RuleParams) => Violation[];
}

export type RuleParams = Record<string, number>;

export interface RuleSet {
  id: string;
  name: string;
  /** 규칙 id → 파라미터. 없으면 규칙 기본값을 쓴다. */
  params: Record<string, RuleParams>;
  /** 끌 규칙 id */
  disabled: string[];
}

export interface RuleReport {
  violations: Violation[];
  errorCount: number;
  warningCount: number;
  /** 검사한 규칙 수 — "아무것도 검사 안 했는데 통과" 를 구분한다 */
  checkedRules: number;
  /** 검사 대상이 없어 건너뛴 규칙 */
  skipped: { ruleId: string; reason: string }[];
}

/* -------------------------------------------------------------------------- *
 * 실행
 * -------------------------------------------------------------------------- */

export function runRules(rules: Rule[], ctx: RuleContext, set: RuleSet): RuleReport {
  const violations: Violation[] = [];
  const skipped: RuleReport['skipped'] = [];
  let checked = 0;

  for (const rule of rules) {
    if (!rule.enabled || set.disabled.includes(rule.id)) {
      skipped.push({ ruleId: rule.id, reason: '꺼져 있음' });
      continue;
    }

    try {
      const found = rule.check(ctx, set.params[rule.id] ?? {});
      violations.push(...found);
      checked++;
    } catch (e) {
      // ★ 규칙 하나가 터져도 나머지 검사는 계속한다.
      //   전부 멈추면 사용자는 "위반 없음" 으로 오해한다 — 가장 위험한 실패다.
      skipped.push({
        ruleId: rule.id,
        reason: `검사 중 오류: ${e instanceof Error ? e.message : String(e)}`,
      });
    }
  }

  // 심각한 것부터. 오류가 경고에 묻히면 안 된다.
  const order: Record<Severity, number> = { error: 0, warning: 1, info: 2 };
  violations.sort((a, b) => order[a.severity] - order[b.severity]);

  return {
    violations,
    errorCount: violations.filter((v) => v.severity === 'error').length,
    warningCount: violations.filter((v) => v.severity === 'warning').length,
    checkedRules: checked,
    skipped,
  };
}

/* -------------------------------------------------------------------------- *
 * 보조
 * -------------------------------------------------------------------------- */

/** 두 사각형이 겹치는가 (경계 접촉은 겹침이 아니다) */
export function overlaps(a: Box, b: Box): boolean {
  return a.minX < b.maxX && b.minX < a.maxX && a.minY < b.maxY && b.minY < a.maxY;
}

/** 겹친 면적 (µm²). 안 겹치면 0 */
export function overlapArea(a: Box, b: Box): number {
  const w = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
  const h = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
  return w > 0 && h > 0 ? w * h : 0;
}

/**
 * 두 사각형 사이의 최단 간격 (µm).
 * 겹쳐 있으면 0. 축 정렬 사각 기준이라 회전한 집기는 보수적으로 나온다.
 */
export function gapBetween(a: Box, b: Box): number {
  const dx = Math.max(0, Math.max(a.minX - b.maxX, b.minX - a.maxX));
  const dy = Math.max(0, Math.max(a.minY - b.maxY, b.minY - a.maxY));
  // 대각으로 떨어져 있으면 실제 거리는 빗변이다
  if (dx > 0 && dy > 0) return Math.hypot(dx, dy);
  return Math.max(dx, dy);
}

/** 두 사각형이 축 방향으로 마주 보는가 (통로를 이루는가) */
export function facesAlong(a: Box, b: Box, axis: 'x' | 'y'): boolean {
  // 통로가 되려면 다른 축으로 겹치는 구간이 있어야 한다
  if (axis === 'x') {
    const overlapY = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
    return overlapY > 0;
  }
  const overlapX = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
  return overlapX > 0;
}

export function boxOf(e: CadEntity, blocks: Map<string, BlockDef>): Box {
  return entityBox(e, blocks);
}

export function unionOf(a: Box, b: Box): Box {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

/** µm → mm, 반올림. 메시지에 쓴다. */
export function mmOf(um: number): number {
  return Math.round(toMm(um));
}
